/**
 * Reusable song player used by the Home feed card and the SongDetail page.
 *
 * Shows the cover, title, artist, lyrics (optionally collapsed), and a
 * single audio element with an A/B variant toggle. Both variants come from
 * the same row; switching variants pauses + swaps `src` (no parallel
 * playback). All audio URLs are served from our assets bucket, never
 * MusicGPT's S3 directly.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { Pause, Play, Music as MusicIcon } from 'lucide-react';
import { useGql } from '@ah/design';
import type { MusicSong } from '../shared/types.ts';
import { RECORD_SONG_ENGAGEMENT_MUTATION } from '../shared/queries.ts';

interface SongPlayerProps {
  song: MusicSong;
  /** When true, lyrics show in full. When false, show only the first ~4 lines with a toggle. */
  expandedLyrics?: boolean;
  /** Hide artist row (useful inside a card that already shows the artist header). */
  hideArtist?: boolean;
  /**
   * Compact card layout for the Home feed: large cover on top, only the primary
   * variant (no A/B toggle). Tight, "one big card" feel.
   */
  variant?: 'default' | 'card';
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SongPlayer({
  song,
  expandedLyrics,
  hideArtist,
  variant: layout = 'default',
}: SongPlayerProps): JSX.Element {
  const gql = useGql();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playRecorded = useRef(false);
  const isCard = layout === 'card';
  // Card layout: lock to the primary playable variant (prefer A) and hide the toggle.
  const initialVariant: 'A' | 'B' = song.variantAAudioUrl ? 'A' : 'B';
  const [variant, setVariant] = useState<'A' | 'B'>(initialVariant);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [errored, setErrored] = useState(false);
  const [lyricsOpen, setLyricsOpen] = useState(isCard ? true : !!expandedLyrics);

  const audioUrl = variant === 'A' ? song.variantAAudioUrl : song.variantBAudioUrl;
  const audioUrlOther = variant === 'A' ? song.variantBAudioUrl : song.variantAAudioUrl;
  const hasOtherVariant = !isCard && !!audioUrlOther;

  useEffect(() => {
    if (!audioUrl) return;
    const audio = new Audio(audioUrl);
    audio.preload = 'metadata';
    audioRef.current = audio;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };
    const onError = () => {
      setErrored(true);
      setPlaying(false);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    setErrored(false);
    setCurrentTime(0);
    setDuration(0);
    setPlaying(false);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [audioUrl]);

  const recordPlay = () => {
    if (playRecorded.current) return;
    playRecorded.current = true;
    gql
      .request(RECORD_SONG_ENGAGEMENT_MUTATION, { id: song.id })
      .catch(() => {
        playRecorded.current = false;
      });
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || errored) return;
    if (audio.paused) {
      const result: unknown = audio.play();
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).then(
          () => {
            setPlaying(true);
            recordPlay();
          },
          () => {
            setErrored(true);
            setPlaying(false);
          },
        );
      } else {
        setPlaying(true);
        recordPlay();
      }
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const progressPct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const fallbackDuration = (variant === 'A' ? song.variantADurationSec : song.variantBDurationSec) ?? null;
  const totalLabel = formatTime(duration > 0 ? duration : fallbackDuration ?? 0);

  return (
    <div className="space-y-4">
      {/* Cover + title */}
      {isCard ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={toggle}
            disabled={!audioUrl || errored}
            aria-label={playing ? 'Pause' : 'Play'}
            className="relative block w-full aspect-square rounded-2xl overflow-hidden bg-muted flex items-center justify-center focus:outline-none group"
          >
            {song.imageUrl ? (
              <img src={song.imageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <MusicIcon size={48} className="text-muted-foreground/50" />
            )}
            <span
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
            >
              <span
                className={`flex items-center justify-center w-16 h-16 rounded-full shadow-lg ${
                  !audioUrl || errored ? 'bg-black/40 text-white/70' : 'bg-black/70 text-white'
                }`}
              >
                {playing ? (
                  <Pause size={30} strokeWidth={1.5} fill="currentColor" />
                ) : (
                  <Play size={30} strokeWidth={1.5} className="ml-1" fill="currentColor" />
                )}
              </span>
            </span>
          </button>
          <div className="min-w-0">
            <p className="font-semibold text-[18px] leading-snug text-foreground">{song.title}</p>
            {!hideArtist && song.artist && (
              <p className="text-[13px] text-muted-foreground mt-0.5 truncate">
                {song.artist.name}
                {song.artist.style ? <span> · {song.artist.style}</span> : null}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex gap-3 items-start">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted shrink-0 flex items-center justify-center">
            {song.imageUrl ? (
              <img src={song.imageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <MusicIcon size={28} className="text-muted-foreground/50" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[17px] leading-snug text-foreground">{song.title}</p>
            {!hideArtist && song.artist && (
              <p className="text-[13px] text-muted-foreground mt-0.5 truncate">
                {song.artist.name}
              </p>
            )}
            <p className="text-[12px] text-muted-foreground/80 mt-1 line-clamp-2">
              {song.artist?.style ?? ''}
            </p>
          </div>
        </div>
      )}

      {/* Player */}
      <div className="flex items-center gap-3">
        {!isCard && (
          <button
            type="button"
            onClick={toggle}
            disabled={!audioUrl || errored}
            aria-label={playing ? 'Pause' : 'Play'}
            className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 transition-colors ${
              !audioUrl || errored
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary/10 text-primary hover:bg-primary/15 active:bg-primary/20'
            }`}
          >
            {playing ? (
              <Pause size={22} className="fill-current" />
            ) : (
              <Play size={22} className="fill-current ml-0.5" />
            )}
          </button>
        )}

        <div className="flex-1 min-w-0">
          {!isCard && (
            <div className="flex items-center justify-between mb-1.5">
              <div
                role="tablist"
                aria-label="Variant"
                className="flex items-center gap-1 bg-muted/60 rounded-full p-0.5"
              >
                {(['A', 'B'] as const).map((v) => {
                  const enabled = v === 'A' ? !!song.variantAAudioUrl : !!song.variantBAudioUrl;
                  const active = variant === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      disabled={!enabled || !hasOtherVariant}
                      onClick={() => setVariant(v)}
                      className={`min-w-[28px] h-6 px-2 rounded-full text-[11px] font-semibold transition-colors ${
                        active
                          ? 'bg-background text-foreground shadow-sm'
                          : enabled
                            ? 'text-muted-foreground hover:text-foreground'
                            : 'text-muted-foreground/40'
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
              <span className="text-[11px] text-muted-foreground tabular-nums">{totalLabel}</span>
            </div>
          )}
          <div className="relative h-4 flex items-center group">
            <div className="absolute inset-x-0 h-1.5 rounded-full bg-muted overflow-hidden pointer-events-none">
              <div
                className={`h-full ${errored ? 'bg-muted-foreground/30' : 'bg-primary'} transition-[width] duration-150`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <input
              type="range"
              min={0}
              max={duration > 0 ? duration : (fallbackDuration ?? 0)}
              step="any"
              value={currentTime}
              disabled={!audioUrl || errored || duration <= 0}
              onChange={(e) => {
                const audio = audioRef.current;
                const t = Number(e.target.value);
                if (audio && Number.isFinite(t)) {
                  audio.currentTime = t;
                  setCurrentTime(t);
                }
              }}
              aria-label="Seek"
              className="relative w-full h-4 appearance-none bg-transparent cursor-pointer disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0"
            />
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{totalLabel}</span>
          </div>
        </div>
      </div>

      {/* Lyrics */}
      {song.lyrics && (
        <div className="rounded-xl bg-muted/40 border border-border/60 p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Lyrics
            </p>
            {!expandedLyrics && !isCard && (
              <button
                type="button"
                onClick={() => setLyricsOpen((o) => !o)}
                className="text-[11px] text-muted-foreground hover:text-foreground"
              >
                {lyricsOpen ? 'Collapse' : 'Show all'}
              </button>
            )}
          </div>
          <pre
            className={`whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-foreground/90 ${
              lyricsOpen || isCard ? '' : 'line-clamp-6'
            }`}
          >
            {song.lyrics}
          </pre>
        </div>
      )}

      {song.status === 'pending' && (
        <p className="text-[12px] text-muted-foreground italic">
          Still composing this track — check back in a minute.
        </p>
      )}
      {errored && (
        <p className="text-[12px] text-destructive">Could not load this variant.</p>
      )}
    </div>
  );
}
